import { useState } from 'react';
import { BarChart, Gauge } from '../components/charts';
import { DashLegend, GlowDonut, TrendLines } from '../../../shared/dashboardCharts';
import { IconAlert, IconClock, IconPrint, IconWrench } from '../components/icons';
import { Badge, Card, EmptyState, Loading, StatCard, useAsync } from '../components/ui';
import { api } from '../lib/api';
import { formatCurrency, formatDate, formatDuration, formatPercent } from '../lib/format';
import { printList, printQuantity } from '../lib/print';

/**
 * Auswertung einer Liegenschaft (Kap. 14).
 *
 * Die Gesamtauswertung beantwortet, wie der Betrieb läuft. Diese hier
 * beantwortet die Fragen, die bei Vertragsverhandlung und Objektübergabe
 * tatsächlich gestellt werden: Was kostet dieses Objekt, wo fällt die Arbeit
 * an, halten wir dort die Fristen – und wiederholt sich etwas?
 *
 * Deshalb sitzt sie in der Objektakte und nicht im Auswertungsmodul: Wer sie
 * sucht, hat das Objekt bereits offen.
 */
export function PropertyAnalytics({ propertyId }: { propertyId: number }) {
  const [months, setMonths] = useState(12);
  const report = useAsync(() => api.get(`/api/reports/property/${propertyId}`, { months }), [propertyId, months]);

  if (report.loading && !report.data) return <Loading />;
  if (!report.data) return <EmptyState title="Auswertung nicht verfügbar" />;

  const { kennzahlen: k, trend, byCriticality, byCategory, byAssignee, topMaterial, recurring, maintenance, property } =
    report.data;

  const zeitraum = months === 0 ? 'seit Beginn' : `letzte ${months} Monate`;

  return (
    <div className="stack">
      <div className="row between">
        <span className="section-title">Auswertung · {zeitraum}</span>
        <div className="row" style={{ gap: 8 }}>
          <label className="toolbar-field">
            <span>Zeitraum</span>
            <select value={months} onChange={(event) => setMonths(Number(event.target.value))}>
              <option value={3}>3 Monate</option>
              <option value={12}>12 Monate</option>
              <option value={24}>24 Monate</option>
              <option value={0}>seit Beginn</option>
            </select>
          </label>
          <button className="btn sm" onClick={() => printReport(report.data, zeitraum)}>
            <IconPrint width={14} height={14} /> Drucken
          </button>
        </div>
      </div>

      <div className="grid cols-4">
        <StatCard
          label="Vorgänge"
          value={k.gesamt}
          tone="primary"
          hint={`${k.offen} offen · ${k.erledigt} erledigt`}
        />
        <StatCard
          label="Gesamtkosten"
          value={formatCurrency(k.gesamtkosten)}
          tone="success"
          hint={`Personal ${formatCurrency(k.personalkosten)} · Material ${formatCurrency(k.materialkosten)}`}
        />
        <StatCard
          label="SLA-Quote"
          value={k.slaQuote != null ? formatPercent(k.slaQuote) : '–'}
          tone={k.slaQuote == null ? '' : k.slaQuote >= 95 ? 'success' : k.slaQuote >= 85 ? 'warning' : 'danger'}
          icon={<IconAlert width={17} height={17} />}
          hint={k.ueberfaellig > 0 ? `${k.ueberfaellig} überfällig` : 'keine Überschreitung offen'}
        />
        <StatCard
          label="Ø bis erledigt"
          value={k.stundenBisErledigt != null ? formatDuration(Math.round(k.stundenBisErledigt * 60)) : '–'}
          tone="info"
          icon={<IconClock width={17} height={17} />}
          hint={`${formatDuration(k.minuten)} erfasste Arbeitszeit`}
        />
      </div>

      {/* Die Kennzahl, nach der ein Eigentümer/Verwalter tatsächlich fragt. */}
      {k.kostenJeEinheit != null && (
        <div className="note-box tone-info">
          <IconWrench width={15} height={15} />
          <span>
            <strong>{formatCurrency(k.kostenJeEinheit)}</strong> je Einheit im gewählten Zeitraum
            {property.units_count ? ` (${property.units_count} Einheiten)` : ''} – Grundlage für Umlage und
            Vertragsgespräch.
          </span>
        </div>
      )}

      <div className="grid cols-2">
        <Card title="Aufkommen je Monat" subtitle="gemeldet gegenüber erledigt">
          {trend.length < 2 ? (
            <EmptyState title="Zu wenig Verlauf" hint="Für eine Kurve braucht es mindestens zwei Monate." />
          ) : (
            <>
              <TrendLines
                ariaLabel="Gemeldete und erledigte Vorgänge je Monat"
                series={[
                  { values: trend.map((row: any) => row.gemeldet), color: 'var(--primary)' },
                  { values: trend.map((row: any) => row.erledigt), color: 'var(--accent)' },
                ]}
              />
              <div className="row between small subtle" style={{ marginTop: 8 }}>
                <span>{trend[0].monat}</span>
                <span>
                  {trend.reduce((sum: number, row: any) => sum + row.gemeldet, 0)} gemeldet ·{' '}
                  {trend.reduce((sum: number, row: any) => sum + row.erledigt, 0)} erledigt
                </span>
                <span>{trend[trend.length - 1].monat}</span>
              </div>
            </>
          )}
        </Card>

        <Card title="Verteilung nach Kritikalität">
          {byCriticality.length === 0 ? (
            <EmptyState title="Keine Vorgänge im Zeitraum" />
          ) : (
            <div className="dash-pair">
              <GlowDonut
                segments={byCriticality.map((row: any) => ({ label: row.name, value: row.anzahl, color: row.color ?? 'var(--text-subtle)' }))}
                centerValue={String(byCriticality.reduce((sum: number, row: any) => sum + row.anzahl, 0))}
                centerLabel="Vorgänge"
              />
              <DashLegend items={byCriticality.map((row: any) => ({ label: row.name, value: String(row.anzahl), color: row.color ?? 'var(--text-subtle)' }))} />
            </div>
          )}
        </Card>
      </div>

      <div className="grid cols-2">
        <Card title="Arbeit nach Gewerk" subtitle="wo die Vorgänge anfallen">
          {byCategory.length === 0 ? (
            <EmptyState title="Keine Zuordnung vorhanden" />
          ) : (
            <BarChart
              data={byCategory.map((row: any) => ({
                id: row.id,
                label: row.name,
                value: row.anzahl,
                color: row.color,
              }))}
            />
          )}
        </Card>

        <Card title="Wer hier arbeitet" subtitle="Grundlage für eine feste Zuständigkeit">
          {byAssignee.length === 0 ? (
            <EmptyState title="Noch keine Zuweisung" />
          ) : (
            <BarChart
              data={byAssignee.map((row: any) => ({
                id: row.id,
                label: `${row.first_name} ${row.last_name}`,
                value: row.anzahl,
              }))}
            />
          )}
        </Card>
      </div>

      {recurring.length > 0 && (
        <Card
          title="Wiederkehrende Stellen"
          subtitle="Dieselbe Sache am selben Ort – Hinweis auf einen Sanierungsfall statt einer Störung"
          noBody
        >
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Gewerk</th>
                  <th>Ort im Objekt</th>
                  <th className="num">Vorgänge</th>
                  <th>Zuletzt</th>
                </tr>
              </thead>
              <tbody>
                {recurring.map((row: any, index: number) => (
                  <tr key={index}>
                    <td>{row.gewerk}</td>
                    <td className="small">{row.ort}</td>
                    <td className="num">
                      <Badge variant={row.anzahl >= 4 ? 'danger' : row.anzahl >= 3 ? 'warning' : ''}>
                        {row.anzahl}
                      </Badge>
                    </td>
                    <td className="small subtle">{formatDate(row.zuletzt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="grid cols-2">
        <Card title="Verbrauchtes Material" subtitle="nach Wert im Zeitraum" noBody>
          {topMaterial.length === 0 ? (
            <div className="card-body">
              <EmptyState title="Kein Material verbucht" />
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Artikel</th>
                    <th className="num">Menge</th>
                    <th className="num">Wert</th>
                  </tr>
                </thead>
                <tbody>
                  {topMaterial.map((row: any) => (
                    <tr key={row.article_no}>
                      <td>
                        {row.name}
                        <div className="small subtle mono">{row.article_no}</div>
                      </td>
                      <td className="num">{printQuantity(row.menge, row.unit)}</td>
                      <td className="num">{formatCurrency(row.wert)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Wartungsstand" subtitle="Prüfpflichten an diesem Objekt" noBody>
          {maintenance.length === 0 ? (
            <div className="card-body">
              <EmptyState
                title="Kein Wartungsplan"
                hint="Für dieses Objekt ist keine wiederkehrende Prüfung hinterlegt."
              />
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Kategorie</th>
                    <th>Nächste Fälligkeit</th>
                  </tr>
                </thead>
                <tbody>
                  {maintenance.map((plan: any) => {
                    const tage = Math.ceil((new Date(plan.next_due_date).getTime() - Date.now()) / 86_400_000);
                    const zustand = tage <= 0 ? 'due' : tage <= (plan.lead_days ?? 14) ? 'soon' : 'ok';
                    return (
                      <tr key={plan.id} style={plan.is_active ? undefined : { opacity: 0.55 }}>
                        <td>
                          {plan.name}
                          {plan.legal_basis && <div className="small subtle">{plan.legal_basis}</div>}
                        </td>
                        <td className="small">
                          {plan.kategorie ? <Badge color={plan.color}>{plan.kategorie}</Badge> : '–'}
                        </td>
                        <td className="small">
                          <Badge variant={zustand === 'due' ? 'danger' : zustand === 'soon' ? 'warning' : ''}>
                            {formatDate(plan.next_due_date)}
                          </Badge>
                          {!plan.is_active && <span className="small subtle"> ausgesetzt</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {k.slaQuote != null && (
        <Card title="Fristtreue" subtitle="Anteil der Vorgänge, die im Zeitraum fristgerecht erledigt wurden">
          <Gauge value={k.slaQuote} label={`${formatPercent(k.slaQuote)} der bewerteten Vorgänge fristgerecht`} />
        </Card>
      )}
    </div>
  );
}

/**
 * Objektbericht für die Akte.
 *
 * Ein Blatt, das man in eine Eigentümerversammlung mitnehmen kann: Kennzahlen
 * oben, die Wiederkehrer als Tabelle. Die Verteilungen bleiben weg – ein
 * Kreisdiagramm auf Papier liest niemand nach.
 */
function printReport(data: any, zeitraum: string) {
  const k = data.kennzahlen;
  const vorgaenge = (anzahl: number) => `${anzahl} ${anzahl === 1 ? 'Vorgang' : 'Vorgänge'}`;
  const rows = [
    ...data.recurring.map((row: any) => ({
      gruppe: 'Wiederkehrend',
      bezeichnung: `${row.gewerk} · ${row.ort}`,
      wert: vorgaenge(row.anzahl),
      zusatz: `zuletzt ${formatDate(row.zuletzt)}`,
    })),
    ...data.byCategory.map((row: any) => ({
      gruppe: 'Nach Gewerk',
      bezeichnung: row.name,
      wert: vorgaenge(row.anzahl),
      zusatz: '',
    })),
    ...data.topMaterial.map((row: any) => ({
      gruppe: 'Material',
      bezeichnung: `${row.name} (${row.article_no})`,
      wert: formatCurrency(row.wert),
      zusatz: printQuantity(row.menge, row.unit),
    })),
    ...data.maintenance.map((row: any) => ({
      gruppe: 'Wartung',
      bezeichnung: row.name,
      wert: formatDate(row.next_due_date),
      zusatz: row.legal_basis ?? '',
    })),
  ];

  printList({
    title: `Objektauswertung – ${data.property.name}`,
    subtitle: [data.property.street, [data.property.zip, data.property.city].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', '),
    meta: [
      { label: 'Zeitraum', value: zeitraum },
      { label: 'Objektnummer', value: data.property.reference_no ?? '–' },
      { label: 'Eigentümer/Verwalter', value: data.property.customer_name ?? '–' },
      { label: 'Vorgänge', value: `${k.gesamt} (${k.offen} offen)` },
      { label: 'Gesamtkosten', value: formatCurrency(k.gesamtkosten) },
      { label: 'SLA-Quote', value: k.slaQuote != null ? formatPercent(k.slaQuote) : '–' },
      { label: 'Arbeitszeit', value: formatDuration(k.minuten) },
      {
        label: 'Kosten je Einheit',
        value: k.kostenJeEinheit != null ? formatCurrency(k.kostenJeEinheit) : '–',
      },
    ],
    columns: [
      { label: 'Bereich', value: (row: any) => row.gruppe },
      { label: 'Bezeichnung', value: (row: any) => row.bezeichnung },
      { label: 'Wert', value: (row: any) => row.wert, align: 'right' },
      { label: 'Anmerkung', value: (row: any) => row.zusatz },
    ],
    rows,
    note:
      'Personalkosten sind mit dem am Zeiteintrag hinterlegten Stundensatz gerechnet, ' +
      'Materialkosten mit dem Einkaufspreis zum Buchungszeitpunkt.',
  });
}
